import React from "react";
import { ShiftThresholds, TimesheetsShiftInfo } from "../../../types/Shift.types";
import { AuditTimestampPairDisplay } from "./AuditTimestampPairDisplay";
import { AuditSection } from "./AuditSection";
import { TimestampRow } from "./styledComponents";
import { getEditAuthor, getEditDate, getEditLabel } from "./utils";
import { useAuditHistoryItems } from "./useAuditHistoryItems";
import { TransformedPairOrDuration } from "./types";
import { isTransformedDuration } from "./utils/auditDisplayUtils";
import { AuditTimestampDurationDisplay } from "./AuditTimestampDurationDisplay";

const breakAppleIcon = "/public/images/icons/Timesheets/break-apple.svg";

export type AuditBreakSectionProps = {
  breakEdits: TransformedPairOrDuration[];
  breakNumber: number;
  timezone: string;
  shiftThresholds: ShiftThresholds;
  shiftKey: string;
  shiftInfo: TimesheetsShiftInfo;
};

/**
 * Displays a meal break audit history section.
 * Shows the most recent edit with option to expand history.
 */
export const AuditBreakSection = ({
  breakEdits,
  breakNumber,
  timezone,
  shiftThresholds,
  shiftKey,
  shiftInfo,
}: AuditBreakSectionProps) => {
  const latestEdit = breakEdits[0];
  const historicalEdits = breakEdits.slice(1);

  const historyItems = useAuditHistoryItems({
    historicalEdits,
    timezone,
    shiftThresholds,
    keyPrefix: `${shiftKey}-break${breakNumber}`,
    shiftInfo,
  });

  const getBreakTitle = () => {
    if (latestEdit.deleted) {
      return `Meal break ${breakNumber} (Deleted)`;
    }
    return `Meal break ${breakNumber}`;
  };

  const latestContent = (
    <TimestampRow>
      {isTransformedDuration(latestEdit) ? (
        <AuditTimestampDurationDisplay
          edit={latestEdit}
          shiftInfo={shiftInfo}
          shiftKey={`${shiftKey}-break${breakNumber}-latest`}
        />
      ) : (
        <AuditTimestampPairDisplay
          edit={latestEdit}
          timezone={timezone}
          shiftThresholds={shiftThresholds}
          shiftKey={`${shiftKey}-break${breakNumber}-latest`}
          isHistoricalDeleted={latestEdit.deleted}
          shiftInfo={shiftInfo}
        />
      )}
    </TimestampRow>
  );

  return (
    <AuditSection
      icon={breakAppleIcon}
      iconAlt="Meal break"
      title={getBreakTitle()}
      label={getEditLabel(latestEdit)}
      date={getEditDate(latestEdit)}
      author={getEditAuthor(latestEdit)}
      deleted={latestEdit.deleted}
      latestContent={latestContent}
      historyItems={historyItems}
    />
  );
};
